import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type {
  ApplicationData,
  MedicationRow,
  AllergyRow,
  AllergyCategory,
  SelfCarryMedType,
  UploadedFileRef,
} from "../types";
import { ALLERGY_CATEGORIES, SELF_CARRY_MED_TYPES } from "../types";
import {
  HEALTH_CONDITIONS,
  defaultHealthConditions,
  normalizeHealthConditionEntry,
} from "../healthConditions";
import * as validation from "../validation";
import { emptyOtcAuthorization, emptyOtcFollowup } from "../otcMedications";
import {
  emptyTopicalItemPermissions,
  emptyTopicalsAuthorization,
} from "../topicalsAuthorization";
import type {
  OtcAuthorization,
  OtcFollowupAnswer,
  OtcSelection,
  OtcPermission,
  TopicalItemPermissions,
  TopicalsAuthorization,
} from "../types";
import {
  APO_OFFICES,
  APO_STATE_CODES,
  emptyMailingAddress,
  normalizeAddressType,
  type ApoOffice,
  type ApoStateCode,
} from "../mailingAddress";
import { usePublicConfig } from "./PublicConfigProvider";

function emptyMed(): MedicationRow {
  return { name: "", strength: "", doseQty: "", doseForm: "", timesOfDay: [], timeOfDayOther: "" };
}
function emptyAllergy(): AllergyRow {
  return { allergy: "", reaction: "", anaphylaxisRisk: null, bringingEpiPen: null, category: "" };
}

function yesNoOrNull(v: unknown): "Yes" | "No" | null {
  return v === "Yes" || v === "No" ? v : null;
}

// Round 12 back-fill: rows saved before the category field exist without it —
// they load with category unanswered (""), never a guessed default.
function normalizeAllergy(a: Partial<AllergyRow> | undefined): AllergyRow {
  return {
    allergy: typeof a?.allergy === "string" ? a.allergy : "",
    reaction: typeof a?.reaction === "string" ? a.reaction : "",
    anaphylaxisRisk: yesNoOrNull(a?.anaphylaxisRisk),
    bringingEpiPen: yesNoOrNull(a?.bringingEpiPen),
    category: (ALLERGY_CATEGORIES as readonly string[]).includes(a?.category as string)
      ? (a!.category as AllergyCategory)
      : "",
  };
}

// Older saved drafts predate the timesOfDay/timeOfDayOther medication shape and
// store rows without those fields. Rebuild every row to the current shape so
// reads (.includes) and validation can't choke on a missing array/string.
function normalizeMed(m: Partial<MedicationRow> | undefined): MedicationRow {
  return {
    name: typeof m?.name === "string" ? m.name : "",
    strength: typeof m?.strength === "string" ? m.strength : "",
    doseQty: typeof m?.doseQty === "string" ? m.doseQty : "",
    doseForm: (m?.doseForm as MedicationRow["doseForm"]) ?? "",
    timesOfDay: Array.isArray(m?.timesOfDay) ? (m!.timesOfDay as MedicationRow["timesOfDay"]) : [],
    timeOfDayOther: typeof m?.timeOfDayOther === "string" ? m.timeOfDayOther : "",
  };
}

// Round 18 back-fill: drafts saved before the OTC block load with an empty,
// unacknowledged authorization (Step 3 correctly reads Incomplete until the
// parent acknowledges + signs). Only well-formed selections are kept.
// Round 19 — coerce a stored follow-up value to an OtcFollowupAnswer. Only a
// well-formed {answer, detail} object is kept; anything else (undefined, or a
// pre-Round-19 plain string) reads as unanswered.
function normalizeFollowup(v: unknown): OtcFollowupAnswer {
  if (!v || typeof v !== "object") return emptyOtcFollowup();
  const o = v as Partial<OtcFollowupAnswer>;
  return {
    answer: yesNoOrNull(o.answer),
    detail: typeof o.detail === "string" ? o.detail : "",
  };
}

function normalizeOtc(o: Partial<OtcAuthorization> | undefined): OtcAuthorization {
  const base = emptyOtcAuthorization();
  if (!o || typeof o !== "object") return base;
  const validPermissions: OtcPermission[] = ["yes", "no", "call"];
  const selections: OtcSelection[] = Array.isArray(o.selections)
    ? o.selections
        .filter(
          (s): s is OtcSelection =>
            !!s &&
            typeof s.id === "string" &&
            validPermissions.includes(s.permission as OtcPermission)
        )
        .map((s) => ({ id: s.id, permission: s.permission }))
    : [];
  return {
    listVersion: typeof o.listVersion === "string" && o.listVersion ? o.listVersion : base.listVersion,
    selections,
    // Round 19 — the follow-ups are Yes/No + detail objects now. Only a
    // well-formed object is kept; anything else (incl. pre-Round-19 plain
    // strings) reads as unanswered. Old TEST drafts are expected to be purged
    // (§3.5), so no string-content migration is attempted.
    // Round 20 — priorOtcReaction / doNotGive / otherNotes were removed. Stored
    // drafts may still carry them; they are simply not read here, so they never
    // reach the app state, the export, or the PDF. No migration needed.
    medAllergies: normalizeFollowup(o.medAllergies),
    acknowledged: o.acknowledged === true,
    acknowledgementSignature:
      typeof o.acknowledgementSignature === "string" ? o.acknowledgementSignature : null,
    acknowledgedAtUtc: typeof o.acknowledgedAtUtc === "string" ? o.acknowledgedAtUtc : null,
  };
}

// Round 20 back-fill: drafts saved before the topicals block load with an empty,
// unacknowledged authorization (Step 3 correctly reads Incomplete until the
// parent acknowledges + signs). Only well-formed permission values are kept;
// anything else reads as blank, which every consumer treats as "No".
function normalizeTopicalPermission(v: unknown): OtcPermission | null {
  return v === "yes" || v === "no" || v === "call" ? v : null;
}

function normalizeTopicalItem(v: unknown): TopicalItemPermissions {
  if (!v || typeof v !== "object") return emptyTopicalItemPermissions();
  const o = v as Partial<TopicalItemPermissions>;
  return {
    give: normalizeTopicalPermission(o.give),
    assist: normalizeTopicalPermission(o.assist),
  };
}

function normalizeTopicals(
  t: Partial<TopicalsAuthorization> | undefined
): TopicalsAuthorization {
  const base = emptyTopicalsAuthorization();
  if (!t || typeof t !== "object") return base;
  return {
    listVersion: typeof t.listVersion === "string" && t.listVersion ? t.listVersion : base.listVersion,
    sunscreen: normalizeTopicalItem(t.sunscreen),
    insectRepellent: normalizeTopicalItem(t.insectRepellent),
    acknowledged: t.acknowledged === true,
    acknowledgementSignature:
      typeof t.acknowledgementSignature === "string" ? t.acknowledgementSignature : null,
    acknowledgedAtUtc: typeof t.acknowledgedAtUtc === "string" ? t.acknowledgedAtUtc : null,
  };
}

function emptySponsor(): ApplicationData["sponsor"] {
  return {
    sponsorConfirmed: false,
    sponsorPostName: "",
    sponsorPostNumber: "",
    sponsorConfirmedDate: "",
  };
}

function normalizeDraft(parsed: ApplicationData): ApplicationData {
  const medications = Array.isArray(parsed.step3?.medications)
    ? parsed.step3.medications.map(normalizeMed)
    : [emptyMed(), emptyMed(), emptyMed()];
  const allergies = Array.isArray(parsed.step3?.allergies)
    ? parsed.step3.allergies.map(normalizeAllergy)
    : [emptyAllergy(), emptyAllergy()];
  // Round 12 back-fill: the self-carry gate loads unanswered on old drafts
  // (Step 3 correctly reads Incomplete until it is answered).
  const selfCarriesEmergencyMeds = yesNoOrNull(parsed.step3?.selfCarriesEmergencyMeds);
  const selfCarryMedTypes = Array.isArray(parsed.step3?.selfCarryMedTypes)
    ? parsed.step3.selfCarryMedTypes.filter((t): t is SelfCarryMedType =>
        (SELF_CARRY_MED_TYPES as readonly string[]).includes(t as string)
      )
    : [];
  const selfCarryMedOther =
    typeof parsed.step3?.selfCarryMedOther === "string" ? parsed.step3.selfCarryMedOther : "";
  // Backfill fields added in Round 4 so drafts saved before this round still load.
  const step1 = {
    sessionYear: parsed.step1?.sessionYear,
    attestationAcknowledged: parsed.step1?.attestationAcknowledged ?? false,
  } as ApplicationData["step1"];
  // Round 22 (R-02) back-fill: a draft saved before the mailing-address redesign
  // has no addressType and none of the APO/Foreign keys. normalizeAddressType
  // reads the missing value as "US", so such a draft loads and behaves exactly
  // as it did before — the new field sets simply start empty and are never
  // validated unless the applicant switches type.
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  const oneOf = <T extends string>(allowed: readonly T[], v: unknown): T | "" =>
    allowed.includes(v as T) ? (v as T) : "";
  const step2 = {
    ...parsed.step2,
    homeCounty: typeof parsed.step2?.homeCounty === "string" ? parsed.step2.homeCounty : "",
    addressType: normalizeAddressType(parsed.step2?.addressType),
    apoRecipientName: str(parsed.step2?.apoRecipientName),
    apoUnitBox: str(parsed.step2?.apoUnitBox),
    apoOffice: oneOf<ApoOffice>(APO_OFFICES, parsed.step2?.apoOffice),
    apoStateCode: oneOf<ApoStateCode>(APO_STATE_CODES, parsed.step2?.apoStateCode),
    apoZip: str(parsed.step2?.apoZip),
    foreignAddressLine1: str(parsed.step2?.foreignAddressLine1),
    foreignAddressLine2: str(parsed.step2?.foreignAddressLine2),
    foreignAddressLine3: str(parsed.step2?.foreignAddressLine3),
    foreignAddressLine4: str(parsed.step2?.foreignAddressLine4),
    foreignAddressLine5: str(parsed.step2?.foreignAddressLine5),
  };
  // Backfill the Round 9 medical-treatment certification so drafts saved before
  // this round still load. A previously "complete" Step 5 correctly becomes
  // Incomplete until the new certification is acknowledged and signed.
  const step5 = {
    ...parsed.step5,
    medicalTreatmentAck: parsed.step5?.medicalTreatmentAck ?? false,
    medicalTreatmentSignature:
      typeof parsed.step5?.medicalTreatmentSignature === "string"
        ? parsed.step5.medicalTreatmentSignature
        : "",
    // Round 17 back-fill: drafts saved before the per-party attestations load
    // unacknowledged (Step 5 correctly reads Incomplete until they are checked).
    applicantCertificationAck: parsed.step5?.applicantCertificationAck ?? false,
    parentCertificationAck: parsed.step5?.parentCertificationAck ?? false,
  } as ApplicationData["step5"];
  const sponsor = parsed.sponsor ?? emptySponsor();
  // Round 11 back-fill: the flat Medical History grid (step3.conditions /
  // conditionOtherDescription) was replaced by the expanded Health History
  // grid. Old drafts load with the new grid unanswered (Step 3 correctly reads
  // Incomplete until answered) — except the old asthma answer, which
  // pre-populates the new asthma row so the Asthma Action Plan requirement is
  // preserved for in-flight drafts.
  const healthConditions = defaultHealthConditions();
  const rawConditions = parsed.step3?.healthConditions;
  if (rawConditions) {
    for (const spec of HEALTH_CONDITIONS) {
      healthConditions[spec.id] = normalizeHealthConditionEntry(rawConditions[spec.id]);
    }
  } else {
    const legacy = (parsed.step3 as unknown as { conditions?: Record<string, unknown> } | undefined)
      ?.conditions;
    if (legacy?.asthma === "Yes" || legacy?.asthma === "No") {
      healthConditions.asthma.answer = legacy.asthma;
    }
  }
  const step3 = {
    ...parsed.step3,
    medications,
    allergies,
    healthConditions,
    selfCarriesEmergencyMeds,
    selfCarryMedTypes,
    selfCarryMedOther,
    topicalsAuthorization: normalizeTopicals(parsed.step3?.topicalsAuthorization),
    otcAuthorization: normalizeOtc(parsed.step3?.otcAuthorization),
  };
  delete (step3 as unknown as Record<string, unknown>).conditions;
  delete (step3 as unknown as Record<string, unknown>).conditionOtherDescription;
  return { ...parsed, step1, step2, step3, step5, sponsor };
}

function makeDefault(program: "boys" | "girls", defaultGender: "Male" | "Female", sessionYear: number): ApplicationData {
  return {
    step1: {
      sessionYear,
      attestationAcknowledged: false,
    },
    step2: {
      isUSCitizen: null,
      isUSLegalResident: null,
      studentEmail: "",
      parentEmail: "",
      legalFirstName: "",
      legalMiddleName: "",
      legalLastName: "",
      suffix: "",
      preferredName: "",
      dob: "",
      ageAtProgram: null,
      gender: defaultGender,
      ...emptyMailingAddress(),
      cellPhone: "",
      highSchoolName: "",
      graduationYear: sessionYear + 1,
      eligibilityJunior: false,
      eligibilitySemesterRemaining: false,
      eligibilityNoOtherProgram: false,
      eligibilityFullSession: false,
      gpa: null,
      classRank: "",
      parent1Name: "",
      parent1Relationship: "",
      parent1Phone: "",
      parent1Email: "",
      parent2Name: "",
      parent2Relationship: "",
      parent2Phone: "",
      parent2Email: "",
      emergencyContactName: "",
      emergencyContactRelationship: "",
      emergencyContactPhone: "",
      tshirtSize: "",
    },
    step3: {
      pcpName: "",
      pcpPhone: "",
      pcpAddress: "",
      healthConditions: defaultHealthConditions(),
      diseases: {
        chickenPox: null,
        germanMeasles: null,
        measles: null,
        mumps: null,
      },
      accommodationRequest: null,
      accommodationDescription: "",
      healthChangesSincePhysical: null,
      healthChangesDescription: "",
      takesMedications: null,
      medications: [emptyMed(), emptyMed(), emptyMed()],
      medicationAckSignature: "",
      medicationAckSignatureDate: "",
      selfCarriesEmergencyMeds: null,
      selfCarryMedTypes: [],
      selfCarryMedOther: "",
      hasAllergies: null,
      allergies: [emptyAllergy(), emptyAllergy()],
      vaccinesUpToDate: null,
      vaccinesComments: "",
      lastPhysicalDate: "",
      topicalsAuthorization: emptyTopicalsAuthorization(),
      otcAuthorization: emptyOtcAuthorization(),
    },
    step4: {
      carrierName: "",
      policyId: "",
      groupNumber: "",
      policyholderName: "",
      policyholderRelationship: "",
      policyholderDob: "",
      claimsPhone: "",
    },
    step5: {
      financialResponsibilityAck: false,
      liabilityWaiverAck: false,
      photoReleaseAck: false,
      photoReleaseSignature: "",
      codeOfConductAck: false,
      codeOfConductSignature: "",
      medicalTreatmentAck: false,
      medicalTreatmentSignature: "",
      parentGuardianSignature: "",
      parentGuardianSignatureDate: "",
      applicantSignature: "",
      applicantSignatureDate: "",
      finalAttestation: false,
      applicantCertificationAck: false,
      parentCertificationAck: false,
    },
    sponsor: {
      sponsorConfirmed: false,
      sponsorPostName: "",
      sponsorPostNumber: "",
      sponsorConfirmedDate: "",
    },
    uploads: {},
    meta: {
      currentStep: 1,
      program,
      submittedAt: null,
      lastAmendedAt: null,
      amendmentCount: 0,
      referenceNumber: null,
      status: "InProgress",
    },
  };
}

export type SaveState = "idle" | "saving" | "saved" | "error" | "offline" | "conflict";

interface ApplicationContextValue {
  data: ApplicationData;
  update: (mut: (d: ApplicationData) => void) => void;
  setStep: (step: number) => void;
  addMedicationRow: () => void;
  removeMedicationRow: (index: number) => void;
  addAllergyRow: () => void;
  removeAllergyRow: (index: number) => void;
  setUpload: (fieldKey: string, ref: UploadedFileRef | null) => void;
  completionPercent: number;
  resumeToken: string | null;
  setResumeToken: (t: string | null) => void;
  isAmendmentMode: boolean;
  setAmendmentMode: (v: boolean) => void;
  // Server draft (SharePoint Drafts list) — the magic-link token, save status,
  // and helpers. localStorage remains the offline/secondary cache.
  draftToken: string | null;
  saveState: SaveState;
  hydrating: boolean;
  hydrateError: boolean;
  /** Ensures a server draft exists (creating/adopting if needed); returns its token. */
  ensureDraft: () => Promise<string | null>;
  /**
   * Like ensureDraft, but reports WHY it didn't yield a token — specifically
   * whether the failure was a definitive duplicate-email conflict (409). The
   * Step 1 gate awaits this and blocks only on `conflict`.
   */
  ensureDraftOutcome: () => Promise<{ ok: boolean; conflict: boolean; token: string | null }>;
  /** Forces an immediate server save; returns the draft token (or null if not savable). */
  saveNow: () => Promise<string | null>;
  // Honeypot (bot protection) — bound to the hidden Step 1 field; sent as a
  // header on draft create so the server can fail silently if a bot fills it.
  botField: string;
  setBotField: (v: string) => void;
}

const EMAIL_RE = /.+@.+\..+/;
const AUTOSAVE_DEBOUNCE_MS = 1500;

/**
 * Backoff schedule (ms) for retrying an autosave PATCH that came back 404. That
 * 404 is almost always transient: the draft was just created but the server
 * instance handling this PATCH can't see it yet (in-memory cache miss on a peer/
 * recycled instance + SharePoint list read lag). Retrying a few times bridges
 * that window so a ~1s blip can't permanently discard the token and brick the
 * session. Only after these are exhausted is the token treated as truly gone.
 * Total ≈ 5s, comfortably past the server's own re-scan window.
 */
export const PATCH_404_RETRY_DELAYS_MS = [750, 1500, 3000];
const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

// Mirror of the server bot time-trap window (MIN_STEP1_ELAPSED_MS in
// draft-service.ts). Kept as a separate client constant so the browser bundle
// never imports the Node-only draft service. If you tune one, tune both.
export const TIME_TRAP_WINDOW_MS = 3000;
const TIME_TRAP_RETRY_BUFFER_MS = 250;

/**
 * Delay before the single automatic retry of a create that the server rejected
 * with the time-trap (429 "too-fast"): the remaining time until the 3s-since-
 * load window closes, plus a small buffer so the retry lands just AFTER it.
 * Never negative.
 */
export function timeTrapRetryDelayMs(
  elapsedMs: number,
  windowMs = TIME_TRAP_WINDOW_MS,
  bufferMs = TIME_TRAP_RETRY_BUFFER_MS
): number {
  return Math.max(0, windowMs - elapsedMs) + bufferMs;
}

/**
 * True only for the bot time-trap rejection (429 with code "too-fast"). The
 * lenient per-IP create rate limit also returns 429 but carries no such code,
 * so it is deliberately NOT auto-retried.
 */
export function isTimeTrapResponse(status: number, code: unknown): boolean {
  return status === 429 && code === "too-fast";
}

function jsonFetch(method: string, path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * "Fresh, live" state is the ONLY state allowed to create a server draft:
 * actively in progress, never submitted, no reference number. Anything else
 * (stub-era localStorage, submitted/amendment states restored without a token)
 * must never be auto-promoted into a new server item.
 */
function isFreshLive(d: ApplicationData): boolean {
  return (
    d.meta.status === "InProgress" &&
    d.meta.submittedAt == null &&
    d.meta.referenceNumber == null
  );
}

const Ctx = createContext<ApplicationContextValue | null>(null);

const LS_KEY_PREFIX = "mdlegion-app-draft:";

export function ApplicationProvider({ children }: { children: React.ReactNode }) {
  const cfg = usePublicConfig();
  const lsKey = `${LS_KEY_PREFIX}${cfg.program}`;

  const [data, setData] = useState<ApplicationData>(() => {
    if (typeof window !== "undefined") {
      const raw = window.localStorage.getItem(lsKey);
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as ApplicationData;
          if (parsed?.meta?.program === cfg.program) return normalizeDraft(parsed);
        } catch {
          // ignore
        }
      }
    }
    return makeDefault(cfg.program, cfg.defaultGender, cfg.sessionYear);
  });

  const [resumeToken, setResumeToken] = useState<string | null>(null);
  const [isAmendmentMode, setAmendmentMode] = useState<boolean>(false);

  // Bot protection: the hidden honeypot value (must stay empty for humans) and
  // the moment Step 1 was loaded (for the server-side time-trap). Both ride on
  // the draft-create request headers.
  const [botField, setBotField] = useState<string>("");
  const botFieldRef = useRef<string>("");
  botFieldRef.current = botField;
  const formLoadedAtRef = useRef<number>(Date.now());

  // --- server draft sync ---------------------------------------------------
  // LINKAGE RULE: the client may only UPDATE a server draft whose token it
  // holds. The token is persisted in localStorage when the draft is created or
  // resumed, so reloads keep their linkage. With no token in hand the client
  // may only CREATE — and only from fresh, live, user-edited state — never
  // adopt or overwrite an existing server item.
  const tokenLsKey = `${LS_KEY_PREFIX}token:${cfg.program}`;
  const [draftToken, setDraftTokenState] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(tokenLsKey);
  });
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [hydrating, setHydrating] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).has("token");
  });
  const [hydrateError, setHydrateError] = useState<boolean>(false);

  // Refs so the debounced saver always reads the latest values without
  // re-subscribing, and so the create path can't run twice concurrently.
  const dataRef = useRef(data);
  dataRef.current = data;
  const tokenRef = useRef<string | null>(draftToken);
  const creatingRef = useRef(false);
  // Set ONLY by user-initiated mutations in this session (update/setStep/
  // uploads/rows). Page load alone — including StrictMode double-mounts and
  // hydration — never marks the draft dirty, so it can never write.
  const dirtyRef = useRef(false);
  // Student email that returned 409 draft-exists; suppresses create retries
  // until the email is changed.
  const conflictEmailRef = useRef<string | null>(null);
  // Pending debounced autosave — cleared whenever an explicit save runs so a
  // stray late PATCH can't land during/after the submission pipeline.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Single pending retry of a time-trapped (429 "too-fast") create. Held so we
  // never stack more than one and can cancel it once a draft exists.
  const timeTrapRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards the one-shot restored-but-unedited create (see effect below).
  const restoredCreateRef = useRef(false);
  const offline = () => typeof navigator !== "undefined" && navigator.onLine === false;

  const setDraftToken = React.useCallback(
    (t: string | null) => {
      tokenRef.current = t;
      setDraftTokenState(t);
      if (typeof window !== "undefined") {
        if (t) window.localStorage.setItem(tokenLsKey, t);
        else window.localStorage.removeItem(tokenLsKey);
      }
    },
    [tokenLsKey]
  );

  const doServerSave = React.useCallback(async (): Promise<string | null> => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const d = dataRef.current;
    const emailsOk = EMAIL_RE.test(d.step2.studentEmail) && EMAIL_RE.test(d.step2.parentEmail);
    if (!emailsOk) return null;
    const token = tokenRef.current;

    if (!token) {
      // CREATE path — only from fresh live state, never for an email that the
      // server already told us has a draft, and never twice concurrently.
      if (!isFreshLive(d)) return null;
      const emailKey = d.step2.studentEmail.trim().toLowerCase();
      if (conflictEmailRef.current === emailKey) return null;
      if (creatingRef.current) return null;
      creatingRef.current = true;
      setSaveState("saving");
      try {
        // Bot protection rides on the create request: the honeypot value (empty
        // for real users) and how long since Step 1 loaded (server time-trap).
        const res = await fetch("/api/drafts", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Form-Website": botFieldRef.current,
            "X-Form-Elapsed-Ms": String(Math.max(0, Date.now() - formLoadedAtRef.current)),
          },
          body: JSON.stringify(dataRef.current),
        });
        if (res.status === 409) {
          conflictEmailRef.current = emailKey;
          setSaveState("conflict");
          return null;
        }
        if (res.status === 429) {
          // Two things return 429: the lenient per-IP create rate limit and the
          // bot time-trap. Only the time-trap (code "too-fast") is auto-retried,
          // exactly once, timed to fire just AFTER the 3s-since-load window so
          // bot protection still holds (no create is attempted before 3s).
          const body = (await res.json().catch(() => null)) as { code?: string } | null;
          if (isTimeTrapResponse(429, body?.code)) {
            if (!timeTrapRetryRef.current) {
              const delay = timeTrapRetryDelayMs(Date.now() - formLoadedAtRef.current);
              timeTrapRetryRef.current = setTimeout(() => {
                timeTrapRetryRef.current = null;
                void doServerSave();
              }, delay);
            }
            setSaveState("saving"); // a retry is queued; not a terminal error
          } else {
            setSaveState(offline() ? "offline" : "error");
          }
          return null;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const summary = (await res.json()) as { token: string };
        // A draft now exists — cancel any queued time-trap retry so it can't
        // fire a redundant late save.
        if (timeTrapRetryRef.current) {
          clearTimeout(timeTrapRetryRef.current);
          timeTrapRetryRef.current = null;
        }
        setDraftToken(summary.token);
        setSaveState("saved");
        return summary.token;
      } catch {
        setSaveState(offline() ? "offline" : "error");
        return null;
      } finally {
        creatingRef.current = false;
      }
    }

    // UPDATE path — we hold this draft's token.
    setSaveState("saving");
    for (let attempt = 0; ; attempt++) {
      try {
        const res = await jsonFetch("PATCH", `/api/drafts/${encodeURIComponent(token)}`, dataRef.current);
        if (res.status === 404) {
          // A 404 right after create is almost always transient — the draft
          // exists but the server instance handling this PATCH can't see it yet
          // (peer/recycled-instance cache miss + SharePoint list read lag).
          // Retry with backoff before giving up so a brief blip can't strand the
          // session; only discard the linkage once the retries are exhausted.
          if (attempt < PATCH_404_RETRY_DELAYS_MS.length) {
            await sleep(PATCH_404_RETRY_DELAYS_MS[attempt]);
            continue;
          }
          setDraftToken(null);
          setSaveState("error");
          return null;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setSaveState("saved");
        return token;
      } catch {
        // Network/other error — never throws to the UI; localStorage holds the
        // work. Don't loop on these (only 404 lag is retried above).
        setSaveState(offline() ? "offline" : "error");
        return token;
      }
    }
  }, [setDraftToken]);

  // Runs a server save and reports whether the student email is a definitive
  // duplicate of an EXISTING application (409). conflictEmailRef is set by the
  // create path on a 409, so a true conflict is detectable synchronously right
  // after the awaited save without waiting for a React re-render of saveState.
  // A resuming/amending user holds a token and takes the UPDATE path, so they
  // get their token back and `conflict` is false.
  const ensureDraftOutcome = React.useCallback(async () => {
    const token = await doServerSave();
    const emailKey = dataRef.current.step2.studentEmail.trim().toLowerCase();
    const conflict = emailKey.length > 0 && conflictEmailRef.current === emailKey;
    return { ok: token != null, conflict, token };
  }, [doServerSave]);

  // Debounced autosave: ONLY for user-edited (dirty) state, only when both
  // emails are valid, never while resuming. Without a token it additionally
  // requires fresh live state (see isFreshLive) so stale/stub-era localStorage
  // can never be auto-promoted to the server by a mere page load.
  useEffect(() => {
    if (hydrating) return;
    if (!dirtyRef.current) return;
    const emailsOk = EMAIL_RE.test(data.step2.studentEmail) && EMAIL_RE.test(data.step2.parentEmail);
    if (!emailsOk) return;
    if (!tokenRef.current) {
      if (!isFreshLive(data)) return;
      if (conflictEmailRef.current === data.step2.studentEmail.trim().toLowerCase()) return;
    }
    const t = setTimeout(() => void doServerSave(), AUTOSAVE_DEBOUNCE_MS);
    debounceRef.current = t;
    return () => {
      clearTimeout(t);
      if (debounceRef.current === t) debounceRef.current = null;
    };
  }, [data, hydrating, doServerSave]);

  // (A) Restored-but-unedited recovery. A localStorage restore (or any non-token
  // page load) can leave valid emails with NO known server draft id while
  // dirtyRef is still false, so the debounced autosave above never fires and no
  // draft is created until the user edits something. Allow EXACTLY ONE create in
  // that specific situation. It is deliberately skipped when a token is already
  // held (including a magic-link/token resume, which already has its draft), and
  // a 409 race is still handled gracefully by doServerSave's create path.
  useEffect(() => {
    if (hydrating) return;
    if (restoredCreateRef.current) return;
    if (dirtyRef.current) return; // a real edit — the debounced autosave owns it
    if (tokenRef.current) return; // already have a draft id (incl. token resume)
    const emailsOk = EMAIL_RE.test(data.step2.studentEmail) && EMAIL_RE.test(data.step2.parentEmail);
    if (!emailsOk) return;
    if (!isFreshLive(data)) return;
    if (conflictEmailRef.current === data.step2.studentEmail.trim().toLowerCase()) return;
    restoredCreateRef.current = true;
    void doServerSave();
  }, [hydrating, data, doServerSave]);

  // Resume hydration: ?token=... (the magic link) loads the draft from the
  // server. SERVER STATE WINS UNCONDITIONALLY over whatever localStorage held,
  // the token is persisted as this browser's linkage, and the draft is NOT
  // marked dirty (loading is not an edit).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/drafts/${encodeURIComponent(token)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const loaded = (await res.json()) as { application: ApplicationData; token: string };
        if (cancelled) return;
        setData(normalizeDraft(loaded.application));
        setDraftToken(loaded.token);
        conflictEmailRef.current = null;
        dirtyRef.current = false;
        // Drop the token from the address bar so it isn't bookmarked/shared.
        window.history.replaceState({}, "", window.location.pathname.replace(/\/resume$/, "/") || "/");
      } catch {
        if (!cancelled) setHydrateError(true);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derived from `data` (and the configured session start) — never stored in
  // state, so it cannot trigger an extra render. On submission it is forced to
  // 100 regardless of the per-step checks.
  const completionPercent = useMemo(() => {
    const submitted =
      data.meta.submittedAt !== null ||
      data.meta.status === "Submitted" ||
      data.meta.status === "Finalized";
    return submitted
      ? 100
      : validation.computeCompletionPercent(data, cfg.sessionStartDate);
  }, [data, cfg.sessionStartDate]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const toPersist: ApplicationData = {
        ...data,
        meta: { ...data.meta, completionPercent },
      };
      window.localStorage.setItem(lsKey, JSON.stringify(toPersist));
    }
  }, [data, lsKey, completionPercent]);

  const value = useMemo<ApplicationContextValue>(
    () => ({
      data,
      update: (mut) => {
        dirtyRef.current = true;
        setData((prev) => {
          const next = structuredClone(prev);
          mut(next);
          return next;
        });
      },
      setStep: (step) => {
        dirtyRef.current = true;
        setData((prev) => ({ ...prev, meta: { ...prev.meta, currentStep: step } }));
      },
      addMedicationRow: () => {
        dirtyRef.current = true;
        setData((prev) => ({
          ...prev,
          step3: { ...prev.step3, medications: [...prev.step3.medications, emptyMed()] },
        }));
      },
      removeMedicationRow: (index) => {
        dirtyRef.current = true;
        setData((prev) => ({
          ...prev,
          step3: {
            ...prev.step3,
            medications: prev.step3.medications.filter((_, i) => i !== index),
          },
        }));
      },
      addAllergyRow: () => {
        dirtyRef.current = true;
        setData((prev) => ({
          ...prev,
          step3: { ...prev.step3, allergies: [...prev.step3.allergies, emptyAllergy()] },
        }));
      },
      removeAllergyRow: (index) => {
        dirtyRef.current = true;
        setData((prev) => ({
          ...prev,
          step3: {
            ...prev.step3,
            allergies: prev.step3.allergies.filter((_, i) => i !== index),
          },
        }));
      },
      setUpload: (fieldKey, ref) => {
        dirtyRef.current = true;
        setData((prev) => {
          const next = { ...prev, uploads: { ...prev.uploads } };
          if (ref) next.uploads[fieldKey] = ref;
          else delete next.uploads[fieldKey];
          return next;
        });
      },
      completionPercent,
      resumeToken,
      setResumeToken,
      isAmendmentMode,
      setAmendmentMode,
      draftToken,
      saveState,
      hydrating,
      hydrateError,
      ensureDraft: doServerSave,
      ensureDraftOutcome,
      saveNow: doServerSave,
      botField,
      setBotField,
    }),
    [
      data,
      completionPercent,
      resumeToken,
      isAmendmentMode,
      draftToken,
      saveState,
      hydrating,
      hydrateError,
      doServerSave,
      ensureDraftOutcome,
      botField,
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApplication(): ApplicationContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useApplication must be used inside ApplicationProvider");
  return v;
}
